import {
  GRID_TREE_DATA_GROUPING_FIELD,
  type GridDataSource,
  type GridGetRowsParams,
  type GridGetRowsResponse,
  type GridSortModel,
  type GridValidRowModel,
} from '@mui/x-data-grid-premium';
import type { ServerGridFilter } from 'src/modules/grids/shared';
import type { CommodityCatalogProduct } from './hooks/useCommoditiesPage';

/**
 * The catalog's rows come from the SERVER in two shapes, told apart by id alone — MUI hands
 * `getGroupKey`/`getChildrenCount` a bare row.
 *
 *   commodity header   id: "c:42"   key "c:42"   ← built from the commodity ID, never its name
 *   product leaf       id: 1337     key "1337"   ← unique among siblings, as MUI requires
 *
 * A product's key is its bare id because the tree's grouping column EXPORTS the leaf key: the
 * catalog has always exported the product id there, and keeping the key identical keeps the file
 * identical. The "c:" prefix means a commodity key can never collide with a product key.
 */
export const COMMODITY_KEY_PREFIX = 'c:';

export const commodityGroupKey = (commodityId: number): string => `${COMMODITY_KEY_PREFIX}${commodityId}`;

export function commodityIdFromGroupKey(groupKey: string): number | undefined {
  if (!groupKey.startsWith(COMMODITY_KEY_PREFIX)) return undefined;
  const id = Number(groupKey.slice(COMMODITY_KEY_PREFIX.length));
  return Number.isInteger(id) ? id : undefined;
}

export type CatalogCommodityRow = { id: string; commodityId: number; name: string; matchingProductCount: number };

export type CatalogProductRow = CommodityCatalogProduct & { description: string };

export type CatalogRow = CatalogCommodityRow | CatalogProductRow;

export const isCommodityRow = (row: GridValidRowModel): row is CatalogCommodityRow =>
  typeof row.id === 'string' && row.id.startsWith(COMMODITY_KEY_PREFIX);

export type CatalogSortItem = { field: string; sort?: 'asc' | 'desc' | null };

/** The scope every request in one browsing session shares. A NEW scope is a NEW data source. */
export type CatalogScope = { searchText?: string; gridFilters: ServerGridFilter[]; quickFilters?: ServerGridFilter[] };

type PageResult<TRow> = { rows: TRow[]; totalCount?: number | null; hasNextPage: boolean; endCursor?: string };

export type CatalogTransport = {
  fetchCommodityPage(args: {
    first: number;
    after?: string;
    scope: CatalogScope;
    sortItems?: CatalogSortItem[];
  }): Promise<PageResult<CatalogCommodityRow>>;
  fetchProductPage(args: {
    first: number;
    after?: string;
    commodityId: number;
    scope: CatalogScope;
    sortItems: CatalogSortItem[];
  }): Promise<PageResult<CatalogProductRow>>;
};

type GroupWindow = { rows: CatalogProductRow[]; endCursor?: string; hasNextPage: boolean; total: number };

export type CatalogDataSource = GridDataSource & {
  /** Loads ONE more bounded batch into an expanded commodity. Cursor-based: no skip, no repeat. */
  loadMore(groupKey: string): Promise<void>;
  /** What an expanded commodity has loaded, against the total the server matched. */
  window(groupKey: string): { shown: number; total: number; hasMore: boolean } | undefined;
  /** The commodity headers the current root page carries. */
  headers(): readonly CatalogCommodityRow[];
};

export type CreateCatalogDataSourceArgs = {
  transport: CatalogTransport;
  scope: CatalogScope;
  /** Commodity headers per root page. */
  rootPageSize: number;
  /** Products per batch inside ONE commodity. */
  childPageSize: number;
  /** Product requests allowed in flight at once — MUI's own child queue is unbounded. */
  maxConcurrentChildRequests?: number;
  onRootLoaded?: (headers: readonly CatalogCommodityRow[]) => void;
  /** A commodity's loaded window grew or was first loaded — drives "Showing N of M". */
  onWindowChanged?: (groupKey: string, window: { shown: number; total: number; hasMore: boolean }) => void;
};

const toSortItems = (sortModel: GridSortModel | undefined): CatalogSortItem[] =>
  (sortModel ?? []).map((item) => ({ field: item.field, sort: item.sort ?? null }));

/** The commodity column (the tree's grouping column) orders the ROOT by commodity name; no other column can. */
const toRootSortItems = (sortModel: GridSortModel | undefined): CatalogSortItem[] | undefined => {
  const sort = sortModel?.find((item) => item.field === GRID_TREE_DATA_GROUPING_FIELD)?.sort;
  return sort ? [{ field: 'name', sort }] : undefined;
};

/**
 * A commodity-first `GridDataSource` for MUI's `treeData + dataSource` strategy.
 *
 *   root      ──► one bounded page of commodity HEADERS, each with its server-matched count
 *   expand    ──► the FIRST bounded batch of THAT commodity's products
 *   loadMore  ──► the next batch, from that commodity's own cursor
 *
 * MUI's nested LAZY loader addresses rows by index (`start - start % pageSize`), which an opaque
 * cursor cannot answer without an offset scan, so this owns a per-commodity window instead.
 * MUI REPLACES a group's rows on every child fetch, so a child response returns the cumulative
 * window — built client-side from cursor batches, never by re-asking for a growing `first`.
 */
export function createCatalogDataSource({
  transport,
  scope,
  rootPageSize,
  childPageSize,
  maxConcurrentChildRequests = 4,
  onRootLoaded,
  onWindowChanged,
}: CreateCatalogDataSourceArgs): CatalogDataSource {
  const windows = new Map<string, GroupWindow>();
  const pendingWindows = new Map<string, Promise<GroupWindow>>();
  let headerRows: CatalogCommodityRow[] = [];
  let windowSortKey = '[]';
  /** Root cursors by row offset, so MUI's numeric page `start` maps onto an opaque cursor. */
  let rootCursorByStart = new Map<number, string | undefined>([[0, undefined]]);
  let rootSortKey = '';

  let inFlight = 0;
  const waiting: Array<() => void> = [];
  async function throttled<T>(task: () => Promise<T>): Promise<T> {
    if (inFlight >= maxConcurrentChildRequests) await new Promise<void>((resolve) => waiting.push(resolve));
    inFlight += 1;
    try {
      return await task();
    } finally {
      inFlight -= 1;
      waiting.shift()?.();
    }
  }

  const totalFor = (groupKey: string) => headerRows.find((row) => row.id === groupKey)?.matchingProductCount ?? 0;

  function fetchBatch(groupKey: string, sortItems: CatalogSortItem[], after?: string) {
    const commodityId = commodityIdFromGroupKey(groupKey);
    if (commodityId === undefined)
      return Promise.resolve({ rows: [], hasNextPage: false } as PageResult<CatalogProductRow>);
    return throttled(() => transport.fetchProductPage({ first: childPageSize, after, commodityId, scope, sortItems }));
  }

  async function firstWindow(groupKey: string, sortItems: CatalogSortItem[]): Promise<GroupWindow> {
    const existing = windows.get(groupKey);
    if (existing) return existing;

    const pending = pendingWindows.get(groupKey);
    if (pending) return pending;

    const request = fetchBatch(groupKey, sortItems).then((page) => {
      const window: GroupWindow = {
        rows: page.rows,
        endCursor: page.endCursor,
        hasNextPage: page.hasNextPage,
        total: totalFor(groupKey),
      };
      windows.set(groupKey, window);
      onWindowChanged?.(groupKey, { shown: window.rows.length, total: window.total, hasMore: window.hasNextPage });
      return window;
    });
    pendingWindows.set(groupKey, request);
    try {
      return await request;
    } finally {
      pendingWindows.delete(groupKey);
    }
  }

  async function rootPage(start: number, sortItems: CatalogSortItem[] | undefined) {
    const sortKey = JSON.stringify(sortItems ?? []);
    if (sortKey !== rootSortKey) {
      rootCursorByStart = new Map([[0, undefined]]);
      rootSortKey = sortKey;
    }
    let knownStart = start;
    while (!rootCursorByStart.has(knownStart) && knownStart > 0) knownStart -= rootPageSize;

    // A jump past what has been walked follows cursors forward — bounded by the commodity count.
    let page = await transport.fetchCommodityPage({
      first: rootPageSize,
      after: rootCursorByStart.get(knownStart),
      scope,
      ...(sortItems ? { sortItems } : {}),
    });
    rootCursorByStart.set(knownStart + page.rows.length, page.endCursor);
    while (knownStart < start && page.hasNextPage) {
      knownStart += page.rows.length;
      page = await transport.fetchCommodityPage({
        first: rootPageSize,
        after: rootCursorByStart.get(knownStart),
        scope,
        ...(sortItems ? { sortItems } : {}),
      });
      rootCursorByStart.set(knownStart + page.rows.length, page.endCursor);
    }
    return page;
  }

  return {
    getGroupKey: (row: GridValidRowModel) => (isCommodityRow(row) ? row.id : String(row.id)),
    getChildrenCount: (row: GridValidRowModel) => (isCommodityRow(row) ? row.matchingProductCount : 0),

    async getRows(params: GridGetRowsParams & { groupKeys?: string[] }): Promise<GridGetRowsResponse> {
      const groupKeys = params.groupKeys ?? [];
      const sortItems = toSortItems(params.sortModel);
      const sortKey = JSON.stringify(sortItems);
      if (sortKey !== windowSortKey) {
        // A window loaded under another order would interleave wrongly with the next batch.
        windows.clear();
        windowSortKey = sortKey;
      }

      if (groupKeys.length === 0) {
        const page = await rootPage(
          typeof params.start === 'number' ? params.start : 0,
          toRootSortItems(params.sortModel)
        );
        headerRows = page.rows;
        onRootLoaded?.(headerRows);
        return { rows: page.rows, rowCount: page.totalCount ?? undefined, pageInfo: { hasNextPage: page.hasNextPage } };
      }

      const groupKey = groupKeys[groupKeys.length - 1] ?? '';
      const window = await firstWindow(groupKey, sortItems);
      // NO `rowCount`: MUI applies a child response's `rowCount` to the ROOT (`setRowCount`), which would
      // replace the commodity total — and the root pagination — with this commodity's product count.
      return { rows: window.rows, pageInfo: { hasNextPage: window.hasNextPage } };
    },

    async loadMore(groupKey: string) {
      const window = windows.get(groupKey);
      if (!window?.hasNextPage) return;

      const page = await fetchBatch(groupKey, JSON.parse(windowSortKey) as CatalogSortItem[], window.endCursor);
      const next = {
        ...window,
        rows: [...window.rows, ...page.rows],
        endCursor: page.endCursor,
        hasNextPage: page.hasNextPage,
      };
      windows.set(groupKey, next);
      onWindowChanged?.(groupKey, { shown: next.rows.length, total: next.total, hasMore: next.hasNextPage });
    },

    window(groupKey: string) {
      const window = windows.get(groupKey);
      return window && { shown: window.rows.length, total: window.total, hasMore: window.hasNextPage };
    },

    headers: () => headerRows,
  };
}
